import type {
  CreateTenantRequest,
  JobDto,
  JobRunDto,
  SupportSessionResponse,
  PlanDto,
  TenantDto,
  TenantModuleDto,
  UpdateTenantRequest,
} from '@an-crm/shared';
import { queryOptions } from '@tanstack/react-query';
import { api } from '@/lib/api/client';

export const platformKeys = {
  tenants: ['platform', 'tenants'] as const,
  tenant: (id: string) => ['platform', 'tenants', id] as const,
  tenantModules: (id: string) => ['platform', 'tenants', id, 'modules'] as const,
  plans: ['platform', 'plans'] as const,
  jobs: ['platform', 'jobs'] as const,
  jobRuns: (key: string) => ['platform', 'jobs', key, 'runs'] as const,
};

export const jobsQuery = queryOptions({
  queryKey: platformKeys.jobs,
  queryFn: ({ signal }) => api<JobDto[]>('/platform/jobs', { signal }),
  refetchInterval: 60_000,
});

export const JOB_RUNS_LIMIT = 50;

export const jobRunsQuery = (key: string) =>
  queryOptions({
    queryKey: platformKeys.jobRuns(key),
    queryFn: ({ signal }) =>
      api<JobRunDto[]>(`/platform/jobs/${encodeURIComponent(key)}/runs`, {
        query: { limit: JOB_RUNS_LIMIT },
        signal,
      }),
  });

export const tenantsQuery = queryOptions({
  queryKey: platformKeys.tenants,
  queryFn: ({ signal }) => api<TenantDto[]>('/platform/tenants', { signal }),
});

export const tenantQuery = (id: string) =>
  queryOptions({
    queryKey: platformKeys.tenant(id),
    queryFn: ({ signal }) => api<TenantDto>(`/platform/tenants/${encodeURIComponent(id)}`, { signal }),
  });

export const tenantModulesQuery = (id: string) =>
  queryOptions({
    queryKey: platformKeys.tenantModules(id),
    queryFn: ({ signal }) =>
      api<TenantModuleDto[]>(`/platform/tenants/${encodeURIComponent(id)}/modules`, { signal }),
  });

export const plansQuery = queryOptions({
  queryKey: platformKeys.plans,
  queryFn: ({ signal }) => api<PlanDto[]>('/platform/plans', { signal }),
  staleTime: 5 * 60_000,
});

export const platformApi = {
  createTenant: (body: CreateTenantRequest) => api<TenantDto>('/platform/tenants', { method: 'POST', body }),
  updateTenant: (id: string, body: UpdateTenantRequest) =>
    api<TenantDto>(`/platform/tenants/${encodeURIComponent(id)}`, { method: 'PATCH', body }),
  setModule: (id: string, key: string, enabled: boolean) =>
    api<TenantModuleDto[]>(`/platform/tenants/${encodeURIComponent(id)}/modules/${encodeURIComponent(key)}`, {
      method: 'PUT',
      body: { enabled },
    }),
  startSupportSession: (tenantId: string) =>
    api<SupportSessionResponse>(`/platform/tenants/${encodeURIComponent(tenantId)}/support-session`, {
      method: 'POST',
    }),
  pauseJob: (key: string) =>
    api<JobDto>(`/platform/jobs/${encodeURIComponent(key)}/pause`, { method: 'POST' }),
  resumeJob: (key: string) =>
    api<JobDto>(`/platform/jobs/${encodeURIComponent(key)}/resume`, { method: 'POST' }),
  runJob: (key: string) =>
    api<{ queued: true }>(`/platform/jobs/${encodeURIComponent(key)}/run`, { method: 'POST' }),
};
